/**
 * ChartTooltip — 차트 hover floating tooltip 공통 컴포넌트.
 *
 * 대시보드 패턴(`.chart-tip floating` 디자인 + 마우스 우측 12px 기본) 표준 + 화면 가장자리
 * 자동 flip 추가. 마우스가 우/하단에 가까우면 반대 방향으로 위치 변경 → 화면 밖으로 안 나감.
 *
 * 사용:
 *   {hover && (
 *     <ChartTooltip x={hover.clientX} y={hover.clientY}>
 *       <div className="tip-label">2026-04-08</div>
 *       <div className="tip-row"><span className="tip-dot" />23,500 원</div>
 *     </ChartTooltip>
 *   )}
 */
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

interface Props {
  /** 마우스 clientX */
  x: number;
  /** 마우스 clientY */
  y: number;
  children: ReactNode;
  /** 마우스로부터 떨어진 거리 (default 12px) */
  gap?: number;
  /** 폭 추정 — 우측 가장자리 flip 판단용 (default 200) */
  estW?: number;
  /** 높이 추정 — 하단 가장자리 flip 판단용 (default 80) */
  estH?: number;
}

export function ChartTooltip({ x, y, children, gap = 12, estW = 200, estH = 80 }: Props) {
  const vw = typeof window !== "undefined" ? window.innerWidth : Number.MAX_SAFE_INTEGER;
  // 기본: 마우스 우측 + 위쪽 — 작은 차트와 겹침 방지
  // flipX: 우측 공간 부족 → 좌측으로 / flipDown: 위쪽 공간 부족 → 아래쪽으로
  const flipX = x + estW + gap > vw;
  const flipDown = y - estH - gap < 0;
  const tx = flipX ? `calc(-100% - ${gap}px)` : `${gap}px`;
  const ty = flipDown ? `${gap}px` : `calc(-100% - ${gap}px)`;
  // Portal: document.body 렌더 — 부모 transform/overflow 영향 받지 않음
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="chart-tip floating"
      style={{
        left: x,
        top: y,
        transform: `translate(${tx}, ${ty})`,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
